import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import DashboardLayout from "@/components/DashboardLayout";
import { VARIETIES, CURRENCIES, BOTTLE_SIZES, CURRENCY_SYMBOL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Wine, Trash2, Pencil, Package, Boxes, Copy, ExternalLink, Phone, Mail, Ticket, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", variety: "extra_virgin", bottle_size: "500ml", price: "", currency: "EUR", stock: "", description: "", image: "", active: true };
const emptyCode = { code: "", type: "percent", value: "", active: true, max_uses: "", expires_at: "" };

export default function Products() {
  const { user } = useAuth();
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [codes, setCodes] = useState([]);
  const [codeForm, setCodeForm] = useState(emptyCode);
  const [codeOpen, setCodeOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/store-orders").then((r) => setOrders(r.data));
    api.get("/discounts").then((r) => setCodes(r.data));
  };
  useEffect(() => { load(); }, []);

  const saveCode = async () => {
    const payload = { ...codeForm, value: parseFloat(codeForm.value) || 0, max_uses: parseInt(codeForm.max_uses) || 0, expires_at: codeForm.expires_at || "" };
    try {
      if (editingCode) await api.put(`/discounts/${editingCode.id}`, payload);
      else await api.post("/discounts", payload);
      toast.success(t("save")); setCodeOpen(false); setCodeForm(emptyCode); setEditingCode(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeCode = async (id) => { await api.delete(`/discounts/${id}`); load(); };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, price: String(p.price), stock: String(p.stock) }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 0 };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(t("product_saved")); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 800;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        if (dataUrl.length > 900000) { toast.error(t("image_too_big")); return; }
        setForm((f) => ({ ...f, image: dataUrl }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const remove = async (id) => { await api.delete(`/products/${id}`); load(); };
  const setOrderStatus = async (o, status) => { await api.put(`/store-orders/${o.id}/status`, { status }); load(); };

  const storeUrl = `${window.location.origin}/store/${user?.slug}`;
  const copyUrl = () => { navigator.clipboard.writeText(storeUrl); toast.success(t("link_copied")); };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-5xl space-y-6" data-testid="products-page">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{t("store_admin_title")}</h1>
            <p className="mt-1 text-muted-foreground">{t("store_admin_sub")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="copy-store-url" variant="outline" onClick={copyUrl} className="rounded-full"><Copy className="mr-1.5 h-4 w-4" /> {t("copy_link")}</Button>
            <Button data-testid="open-store-btn" variant="outline" onClick={() => window.open(storeUrl, "_blank")} className="rounded-full"><ExternalLink className="h-4 w-4" /></Button>
          </div>
        </div>

        <Tabs defaultValue="products">
          <TabsList>
            <TabsTrigger value="products" data-testid="tab-products"><Wine className="mr-1.5 h-4 w-4" /> {t("tab_products")}</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders"><Package className="mr-1.5 h-4 w-4" /> {t("tab_orders")} {orders.length > 0 && <span className="ml-1.5 rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground">{orders.length}</span>}</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts"><Ticket className="mr-1.5 h-4 w-4" /> {t("tab_discounts")}</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-4">
            <Button data-testid="add-product-btn" onClick={openNew} className="mb-4 rounded-full bg-primary hover:bg-olive-hover"><Plus className="mr-1.5 h-4 w-4" /> {t("add_product")}</Button>
            {products.length ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {products.map((p) => (
                  <div key={p.id} data-testid={`product-card-${p.id}`} className="overflow-hidden rounded-xl border border-border bg-card">
                    {p.image ? (
                      <img src={p.image} alt={p.name} data-testid={`product-image-${p.id}`} className="h-36 w-full object-cover" />
                    ) : (
                      <div className="flex h-36 w-full items-center justify-center bg-amber-glow text-amber-deep"><Wine className="h-9 w-9" /></div>
                    )}
                    <div className="p-5">
                    <div className="flex items-center justify-end">
                      <span className="rounded-full bg-olive-light px-2 py-0.5 text-[10px] font-semibold text-olive">{t(`variety_${p.variety}`)}</span>
                    </div>
                    <p className="mt-3 font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.bottle_size} · {p.stock} {t("in_stock")}</p>
                    <p className="mt-2 font-heading text-xl font-bold">{CURRENCY_SYMBOL[p.currency]}{p.price}</p>
                    <div className="mt-4 flex gap-2">
                      <Button data-testid={`edit-product-${p.id}`} size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1"><Pencil className="mr-1 h-3.5 w-3.5" /> {t("edit")}</Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button data-testid={`delete-product-${p.id}`} size="icon" variant="outline" className="h-9 w-9 text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>{t("delete")} {p.name}?</AlertDialogTitle></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>{t("cancel")}</AlertDialogCancel><AlertDialogAction onClick={() => remove(p.id)} className="bg-destructive hover:bg-destructive/90">{t("delete")}</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="rounded-xl border border-dashed border-border py-16 text-center"><Wine className="mx-auto h-10 w-10 text-muted-foreground/40" /><p className="mt-3 text-sm text-muted-foreground">{t("no_products")}</p></div>}
          </TabsContent>

          <TabsContent value="orders" className="mt-4">
            {orders.length ? (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`store-order-${o.id}`} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold">{o.customer_name} <span className="text-muted-foreground">· {o.quantity}× {o.product_name}{o.amount ? ` · €${Number(o.amount).toFixed(2)}` : ""}</span></p>
                      <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {o.customer_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {o.customer_phone}</span>}
                        {o.customer_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {o.customer_email}</span>}
                      </div>
                      {o.note && <p className="mt-1 text-xs italic text-muted-foreground">"{o.note}"</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={o.status} t={t} />
                      {o.status === "requested" && <Button size="sm" data-testid={`confirm-order-${o.id}`} onClick={() => setOrderStatus(o, "confirmed")} className="bg-primary hover:bg-olive-hover">{t("mark_confirmed")}</Button>}
                      {(o.status === "confirmed" || o.status === "paid") && <Button size="sm" data-testid={`fulfill-order-${o.id}`} onClick={() => setOrderStatus(o, "fulfilled")} className="bg-primary hover:bg-olive-hover">{t("mark_fulfilled")}</Button>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="rounded-xl border border-dashed border-border py-16 text-center"><Package className="mx-auto h-10 w-10 text-muted-foreground/40" /><p className="mt-3 text-sm text-muted-foreground">{t("no_store_orders")}</p></div>}
          </TabsContent>

          <TabsContent value="discounts" className="mt-4">
            <Button data-testid="add-code-btn" onClick={() => { setEditingCode(null); setCodeForm(emptyCode); setCodeOpen(true); }} className="mb-4 rounded-full bg-primary hover:bg-olive-hover"><Plus className="mr-1.5 h-4 w-4" /> {t("add_code")}</Button>
            {codes.length ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {codes.map((c) => (
                  <div key={c.id} data-testid={`code-card-${c.id}`} className="rounded-xl border border-border bg-card p-5">
                    <div className="flex items-start justify-between">
                      <span className="rounded-lg bg-olive-light px-3 py-1.5 font-mono text-sm font-bold text-olive">{c.code}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.active ? "bg-green-100 text-green-700" : "bg-secondary text-muted-foreground"}`}>{c.active ? t("active") : t("disabled")}</span>
                    </div>
                    <p className="mt-3 font-heading text-xl font-bold">{c.type === "percent" ? `${c.value}% ${t("percent_off").replace("% ", "")}` : `€${c.value} ${t("fixed_off").replace("Fixed ", "").replace("Fikse ", "")}`}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{c.max_uses ? `${c.uses || 0}/${c.max_uses} ${t("uses_label")}` : `${c.uses || 0} ${t("uses_label")}`}{c.expires_at ? ` · ${t("expiry_date")}: ${c.expires_at}` : ""}</p>
                    <div className="mt-4 flex gap-2">
                      <Button data-testid={`edit-code-${c.id}`} size="sm" variant="outline" onClick={() => { setEditingCode(c); setCodeForm({ code: c.code, type: c.type, value: String(c.value), active: c.active, max_uses: String(c.max_uses || ""), expires_at: c.expires_at || "" }); setCodeOpen(true); }} className="flex-1"><Pencil className="mr-1 h-3.5 w-3.5" /> {t("edit")}</Button>
                      <Button data-testid={`delete-code-${c.id}`} size="icon" variant="outline" onClick={() => removeCode(c.id)} className="h-9 w-9 text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="rounded-xl border border-dashed border-border py-16 text-center"><Ticket className="mx-auto h-10 w-10 text-muted-foreground/40" /><p className="mt-3 text-sm text-muted-foreground">{t("no_codes")}</p></div>}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_product")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{t("product_name")}</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>{t("product_image")} ({t("optional")})</Label>
              <div className="flex items-center gap-3">
                {form.image ? (
                  <div className="relative">
                    <img src={form.image} alt="" data-testid="product-image-preview" className="h-16 w-16 rounded-lg object-cover" />
                    <button type="button" data-testid="remove-product-image" onClick={() => setForm({ ...form, image: "" })} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white"><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-amber-glow text-amber-deep"><Wine className="h-6 w-6" /></div>
                )}
                <label data-testid="upload-product-image" className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary">
                  <ImagePlus className="h-4 w-4" /> {t("upload_image")}
                  <input type="file" accept="image/*" className="hidden" onChange={onImageFile} />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">{t("image_hint")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("variety")}</Label>
                <Select value={form.variety} onValueChange={(v) => setForm({ ...form, variety: v })}>
                  <SelectTrigger data-testid="product-variety"><SelectValue /></SelectTrigger>
                  <SelectContent>{VARIETIES.map((v) => <SelectItem key={v} value={v}>{t(`variety_${v}`)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>{t("bottle_size")}</Label>
                <Select value={form.bottle_size} onValueChange={(v) => setForm({ ...form, bottle_size: v })}>
                  <SelectTrigger data-testid="product-size"><SelectValue /></SelectTrigger>
                  <SelectContent>{BOTTLE_SIZES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>{t("price")}</Label><Input data-testid="product-price" type="number" step="0.1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("currency")}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="product-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>{t("stock")}</Label><Input data-testid="product-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>{t("description")}</Label><Textarea data-testid="product-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-active" /> {t("active_label")}</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-product-btn" onClick={save} disabled={!form.name} className="bg-primary hover:bg-olive-hover">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent data-testid="code-dialog">
          <DialogHeader><DialogTitle>{editingCode ? t("edit") : t("add_code")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{t("code_label")}</Label><Input data-testid="code-input" value={codeForm.code} onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value.toUpperCase() })} placeholder="HARVEST10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("discount_type")}</Label>
                <Select value={codeForm.type} onValueChange={(v) => setCodeForm({ ...codeForm, type: v })}>
                  <SelectTrigger data-testid="code-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">{t("percent_off")}</SelectItem><SelectItem value="fixed">{t("fixed_off")}</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>{t("value_label")}</Label><Input data-testid="code-value" type="number" step="0.5" value={codeForm.value} onChange={(e) => setCodeForm({ ...codeForm, value: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("max_uses")}</Label><Input data-testid="code-maxuses" type="number" min="0" value={codeForm.max_uses} onChange={(e) => setCodeForm({ ...codeForm, max_uses: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("expiry_date")}</Label><Input data-testid="code-expiry" type="date" value={codeForm.expires_at} onChange={(e) => setCodeForm({ ...codeForm, expires_at: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={codeForm.active} onCheckedChange={(v) => setCodeForm({ ...codeForm, active: v })} data-testid="code-active" /> {t("active_label")}</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCodeOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-code-btn" onClick={saveCode} disabled={!codeForm.code || !codeForm.value} className="bg-primary hover:bg-olive-hover">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function StatusBadge({ status, t }) {
  const colors = { requested: "#F59E0B", confirmed: "#3B82F6", fulfilled: "#059669", paid: "#059669", pending_payment: "#F59E0B" };
  return <span className="rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: `${colors[status]}1a`, color: colors[status] }}>{t(status)}</span>;
}
